import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import { useUser } from '../context/UserContext';
import { getStudent, getCourses, getPayments, getGrades, getCommunications, getStudentDocuments, uploadStudentDocument, deleteStudentDocument, updateStudent, deleteStudent, chargeCard, createStandingOrder, createPaymentLink, createSignatureRequest, getSignatures, initiateCall, scheduleCall, getVoicenterCalls, API_ORIGIN } from '../services/api';
import TasksTab from '../components/TasksTab';
import ContextMenu from '../components/ContextMenu';
import TaskFromContextModal from '../components/TaskFromContextModal';

export default function StudentDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const { userName } = useUser();
  const [student, setStudent] = useState<any>(null);
  const [courses, setCourses] = useState<any[]>([]);
  const [payments, setPayments] = useState<any[]>([]);
  const [grades, setGrades] = useState<any[]>([]);
  const [comms, setComms] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState('info');
  const [allCourses, setAllCourses] = useState<any[]>([]);
  const [documents, setDocuments] = useState<any[]>([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadDocType, setUploadDocType] = useState('other');
  const [uploadNotes, setUploadNotes] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<any>({});
  const [successMsg, setSuccessMsg] = useState('');
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; type: string; item: any } | null>(null);
  const [ctxTaskModal, setCtxTaskModal] = useState<{ sourceType: 'document' | 'communication'; sourceName: string; sourceLink?: string } | null>(null);
  const [showChargeModal, setShowChargeModal] = useState(false);
  const [showStandingOrderModal, setShowStandingOrderModal] = useState(false);
  const [showPaymentLinkModal, setShowPaymentLinkModal] = useState(false);
  const [generatedLink, setGeneratedLink] = useState('');
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signatures, setSignatures] = useState<any[]>([]);
  const [chargeForm, setChargeForm] = useState({ amount: 0, installments: 1, description: '' });
  const [soForm, setSoForm] = useState({ monthlyAmount: 0, collectionDay: 1, numberOfMonths: 12, bankNumber: '', branchNumber: '', accountNumber: '', accountHolder: '' });
  const [plForm, setPlForm] = useState({ amount: 0, description: '' });
  const [sigForm, setSigForm] = useState({ documentId: '', documentName: '', signerName: '', signerEmail: '', signerPhone: '', message: '' });
  const [callHistory, setCallHistory] = useState<any[]>([]);
  const [showScheduleCallModal, setShowScheduleCallModal] = useState(false);
  const [scheduleCallForm, setScheduleCallForm] = useState({ scheduledDate: '', scheduledTime: '' });

  useEffect(() => {
    if (!id) return;
    getStudent(id).then(res => setStudent(res.data)).catch(() => {});
    getPayments({ studentId: id }).then(res => setPayments(res.data)).catch(() => {});
    getGrades({ studentId: id }).then(res => setGrades(res.data)).catch(() => {});
    getCommunications({ recipientId: id }).then(res => setComms(res.data)).catch(() => {});
    getCourses().then(res => setAllCourses(res.data)).catch(() => {});
    getStudentDocuments(id).then(res => setDocuments(res.data)).catch(() => {});
    getSignatures({ entityType: 'student', entityId: id }).then(res => setSignatures(res.data)).catch(() => {});
    getVoicenterCalls({ entityType: 'student', entityId: id }).then(res => setCallHistory(res.data)).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (student && allCourses.length > 0) {
      setCourses(allCourses.filter(c => student.courses.includes(c.id)));
    }
  }, [student, allCourses]);

  if (!student) return <div className="page-section"><p>{t('loading')}</p></div>;

  const getName = () => {
    if (lang === 'ar' && student.firstNameAr) return `${student.firstNameAr} ${student.lastNameAr}`;
    return `${student.firstName} ${student.lastName}`;
  };

  const statusText = (s: string) => {
    const map: Record<string, string> = {
      candidate: t('candidate'), approved: t('approved'), active: t('active'),
      frozen: t('frozen'), graduated: t('graduated')
    };
    return map[s] || s;
  };

  const statusColors: Record<string, string> = {
    candidate: 'badge-blue', approved: 'badge-gold', active: 'badge-green',
    frozen: 'badge-yellow', graduated: 'badge-gray',
  };

  const payStatusColors: Record<string, string> = {
    paid: 'badge-green', partial: 'badge-yellow', debt: 'badge-red'
  };

  const openWhatsApp = () => {
    const phone = student.phone.replace(/[^0-9]/g, '');
    window.open(`https://wa.me/972${phone.startsWith('0') ? phone.slice(1) : phone}`, '_blank');
  };

  const openEmail = () => {
    window.open(`mailto:${student.email}`, '_blank');
  };

  const docTypeKeys = ['id_card', 'bagrut', 'prev_studies', 'medical', 'passport_photo', 'registration_contract', 'receipt', 'other'];

  const getDocTypeName = (key: string) => {
    return t(`docTypes.${key}`);
  };

  const handleUploadDocument = async () => {
    if (!uploadFile || !id) return;
    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('docType', uploadDocType);
    formData.append('notes', uploadNotes);
    formData.append('performedBy', userName);
    try {
      await uploadStudentDocument(id, formData);
      const res = await getStudentDocuments(id);
      setDocuments(res.data);
      setShowUploadModal(false);
      setUploadFile(null);
      setUploadDocType('other');
      setUploadNotes('');
    } catch (err) {
      console.error('Upload failed', err);
    }
  };

  const handleDeleteDocument = async (docId: string) => {
    if (!id) return;
    try {
      await deleteStudentDocument(id, docId);
      setDocuments(documents.filter(d => d.id !== docId));
    } catch (err) {
      console.error('Delete failed', err);
    }
  };

  const handleEditStudent = () => {
    setEditForm({
      firstName: student.firstName || '',
      lastName: student.lastName || '',
      firstNameAr: student.firstNameAr || '',
      lastNameAr: student.lastNameAr || '',
      email: student.email || '',
      phone: student.phone || '',
      address: student.address || '',
      city: student.city || '',
      dateOfBirth: student.dateOfBirth || '',
      nationalId: student.nationalId || '',
      enrollmentDate: student.enrollmentDate || '',
      status: student.status || '',
      program: student.program || '',
      notes: student.notes || '',
    });
    setIsEditing(true);
    setSuccessMsg('');
  };

  const handleSaveStudent = async () => {
    if (!id) return;
    try {
      await updateStudent(id, editForm);
      const res = await getStudent(id);
      setStudent(res.data);
      setIsEditing(false);
      setSuccessMsg('הנתונים נשמרו בהצלחה!');
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      console.error('Save failed', err);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditForm({});
  };

  const formatPhoneForWhatsApp = (phone: string) => {
    const cleaned = phone.replace(/[^0-9]/g, '');
    return cleaned.startsWith('0') ? '972' + cleaned.slice(1) : '972' + cleaned;
  };

  const handleChargeCard = async () => {
    if (!id) return;
    await chargeCard({ studentId: id, ...chargeForm, performedBy: userName });
    setShowChargeModal(false);
    alert('התשלום בוצע בהצלחה!');
  };

  const handleCreateStandingOrder = async () => {
    if (!id) return;
    await createStandingOrder({ studentId: id, ...soForm, performedBy: userName });
    setShowStandingOrderModal(false);
    alert('הוראת הקבע הוקמה בהצלחה!');
  };

  const handleCreatePaymentLink = async () => {
    if (!id) return;
    const res = await createPaymentLink({ studentId: id, ...plForm, performedBy: userName });
    setGeneratedLink(`${window.location.origin}/pay/${res.data.id}`);
  };

  const handleSendForSignature = async () => {
    if (!id) return;
    await createSignatureRequest({ entityType: 'student', entityId: id, ...sigForm, performedBy: userName });
    setShowSignatureModal(false);
    getSignatures({ entityType: 'student', entityId: id }).then(res => setSignatures(res.data)).catch(() => {});
    alert('בקשת החתימה נשלחה בהצלחה!');
  };

  const handleInitiateCall = async () => {
    if (!id || !student.phone) return;
    await initiateCall({ phoneNumber: student.phone, entityType: 'student', entityId: id, entityName: getName(), calledBy: userName });
    alert(lang === 'ar' ? 'بدأت المكالمة!' : 'השיחה התחילה!');
    getVoicenterCalls({ entityType: 'student', entityId: id }).then(res => setCallHistory(res.data)).catch(() => {});
  };

  const handleScheduleCall = async () => {
    if (!id || !student.phone) return;
    await scheduleCall({ phoneNumber: student.phone, entityType: 'student', entityId: id, entityName: getName(), calledBy: userName, ...scheduleCallForm });
    setShowScheduleCallModal(false);
    setScheduleCallForm({ scheduledDate: '', scheduledTime: '' });
    alert(lang === 'ar' ? 'تم جدولة المكالمة!' : 'השיחה תוזמנה!');
    getVoicenterCalls({ entityType: 'student', entityId: id }).then(res => setCallHistory(res.data)).catch(() => {});
  };

  const handleDeleteStudent = async () => {
    if (!id) return;
    if (window.confirm('האם אתה בטוח שברצונך למחוק?')) {
      try {
        await deleteStudent(id);
        navigate('/students');
      } catch (err) {
        console.error('Delete failed', err);
      }
    }
  };

  return (
    <div>
      <div className="page-section">
        <div className="detail-header">
          <div className="detail-avatar">{student.firstName[0]}</div>
          <div className="detail-info">
            <h2>{getName()}</h2>
            <p dir="ltr">{student.email} | {student.phone}</p>
            <span className={`badge ${statusColors[student.status]}`}>{statusText(student.status)}</span>
          </div>
          <div className="detail-actions">
            <button className="btn btn-sm" style={{ background: '#4CAF50', color: '#fff', border: 'none' }} onClick={handleInitiateCall}>📞 {t('callNow')}</button>
            <button className="btn btn-sm btn-whatsapp" onClick={openWhatsApp}>📱 {t('sendWhatsApp')}</button>
            <button className="btn btn-sm btn-email" onClick={openEmail}>📧 {t('sendEmail')}</button>
            <Link to="/students" className="btn btn-sm btn-outline">← {t('back')}</Link>
          </div>
        </div>

        {/* Edit/Save/Cancel/Delete Buttons */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {!isEditing && (
            <button onClick={handleEditStudent} style={{ padding: '8px 20px', borderRadius: 6, border: 'none', color: '#fff', fontWeight: 600, cursor: 'pointer', background: '#2196F3' }}>
              עריכה
            </button>
          )}
          {isEditing && (
            <button onClick={handleSaveStudent} style={{ padding: '8px 20px', borderRadius: 6, border: 'none', color: '#fff', fontWeight: 600, cursor: 'pointer', background: '#4CAF50' }}>
              שמירה
            </button>
          )}
          {isEditing && (
            <button onClick={handleCancelEdit} style={{ padding: '8px 20px', borderRadius: 6, border: 'none', color: '#fff', fontWeight: 600, cursor: 'pointer', background: '#9E9E9E' }}>
              ביטול
            </button>
          )}
          <button onClick={handleDeleteStudent} style={{ padding: '8px 20px', borderRadius: 6, border: 'none', color: '#fff', fontWeight: 600, cursor: 'pointer', background: '#f44336' }}>
            מחיקה
          </button>
        </div>
        {successMsg && (
          <div style={{ background: '#e8f5e9', color: '#2e7d32', padding: '10px 16px', borderRadius: 6, marginBottom: 12, fontWeight: 500 }}>
            {successMsg}
          </div>
        )}

        <div className="tabs">
          <button className={`tab ${activeTab === 'info' ? 'active' : ''}`} onClick={() => setActiveTab('info')}>{t('details')}</button>
          <button className={`tab ${activeTab === 'courses' ? 'active' : ''}`} onClick={() => setActiveTab('courses')}>{t('enrolledCourses')}</button>
          <button className={`tab ${activeTab === 'grades' ? 'active' : ''}`} onClick={() => setActiveTab('grades')}>{t('grades')}</button>
          <button className={`tab ${activeTab === 'payments' ? 'active' : ''}`} onClick={() => setActiveTab('payments')}>{t('payments')}</button>
          <button className={`tab ${activeTab === 'comm' ? 'active' : ''}`} onClick={() => setActiveTab('comm')}>{t('commHistory')}</button>
          <button className={`tab ${activeTab === 'calls' ? 'active' : ''}`} onClick={() => setActiveTab('calls')}>📞 {t('calls')}</button>
          <button className={`tab ${activeTab === 'documents' ? 'active' : ''}`} onClick={() => setActiveTab('documents')}>📄 {t('documents')}</button>
          <button className={`tab ${activeTab === 'tasks' ? 'active' : ''}`} onClick={() => setActiveTab('tasks')}>📋 משימות</button>
        </div>

        {activeTab === 'info' && !isEditing && (
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">{t('firstName')}</label>
              <div className="form-input" style={{ background: '#f8f9fa' }}>{student.firstName}</div>
            </div>
            <div className="form-group">
              <label className="form-label">{t('lastName')}</label>
              <div className="form-input" style={{ background: '#f8f9fa' }}>{student.lastName}</div>
            </div>
            <div className="form-group">
              <label className="form-label">{t('email')}</label>
              <div className="form-input" style={{ background: '#f8f9fa', direction: 'ltr', textAlign: 'left' }}>{student.email}</div>
            </div>
            <div className="form-group">
              <label className="form-label">{t('phone')}</label>
              <div className="form-input" style={{ background: '#f8f9fa', direction: 'ltr', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8 }}>
                {student.phone}
                <button onClick={handleInitiateCall} style={{ background: '#4CAF50', color: '#fff', border: 'none', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontSize: 12 }}>📞 {t('callNow')}</button>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">{t('address')}</label>
              <div className="form-input" style={{ background: '#f8f9fa' }}>{student.address || '-'}</div>
            </div>
            <div className="form-group">
              <label className="form-label">{t('dateOfBirth')}</label>
              <div className="form-input" style={{ background: '#f8f9fa' }}>{student.dateOfBirth || '-'}</div>
            </div>
            <div className="form-group">
              <label className="form-label">{t('nationalId')}</label>
              <div className="form-input" style={{ background: '#f8f9fa' }}>{student.nationalId || '-'}</div>
            </div>
            <div className="form-group">
              <label className="form-label">{t('enrollmentDate')}</label>
              <div className="form-input" style={{ background: '#f8f9fa' }}>{student.enrollmentDate}</div>
            </div>
          </div>
        )}

        {activeTab === 'info' && isEditing && (
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">{t('firstName')}</label>
              <input className="form-input" value={editForm.firstName} onChange={e => setEditForm({...editForm, firstName: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('lastName')}</label>
              <input className="form-input" value={editForm.lastName} onChange={e => setEditForm({...editForm, lastName: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('firstName')} (ערבית)</label>
              <input className="form-input" value={editForm.firstNameAr} onChange={e => setEditForm({...editForm, firstNameAr: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('lastName')} (ערבית)</label>
              <input className="form-input" value={editForm.lastNameAr} onChange={e => setEditForm({...editForm, lastNameAr: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('email')}</label>
              <input className="form-input" type="email" value={editForm.email} onChange={e => setEditForm({...editForm, email: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('phone')}</label>
              <input className="form-input" type="tel" value={editForm.phone} onChange={e => setEditForm({...editForm, phone: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('address')}</label>
              <input className="form-input" value={editForm.address} onChange={e => setEditForm({...editForm, address: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('city')}</label>
              <input className="form-input" value={editForm.city} onChange={e => setEditForm({...editForm, city: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('dateOfBirth')}</label>
              <input className="form-input" type="date" value={editForm.dateOfBirth} onChange={e => setEditForm({...editForm, dateOfBirth: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('nationalId')}</label>
              <input className="form-input" value={editForm.nationalId} onChange={e => setEditForm({...editForm, nationalId: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('enrollmentDate')}</label>
              <input className="form-input" type="date" value={editForm.enrollmentDate} onChange={e => setEditForm({...editForm, enrollmentDate: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('notes')}</label>
              <textarea className="form-textarea" value={editForm.notes} onChange={e => setEditForm({...editForm, notes: e.target.value})} />
            </div>
          </div>
        )}

        {activeTab === 'courses' && (
          <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('courseCode')}</th>
                <th>{t('courseName')}</th>
                <th>{t('lecturer')}</th>
                <th>{t('semester')}</th>
              </tr>
            </thead>
            <tbody>
              {courses.map(c => (
                <tr key={c.id}>
                  <td>{c.code}</td>
                  <td><Link to={`/courses/${c.id}`} style={{ color: '#7B1FA2', textDecoration: 'none' }}>{lang === 'ar' && c.nameAr ? c.nameAr : c.name}</Link></td>
                  <td>{c.lecturerId}</td>
                  <td>{c.semester} - {c.year}</td>
                </tr>
              ))}
              {courses.length === 0 && <tr><td colSpan={4} style={{ textAlign: 'center', color: '#999' }}>{t('noData')}</td></tr>}
            </tbody>
          </table>
          </div>
        )}

        {activeTab === 'grades' && (
          <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('courseName')}</th>
                <th>סוג בחינה</th>
                <th>ציון</th>
                <th>{t('date')}</th>
              </tr>
            </thead>
            <tbody>
              {grades.map(g => {
                const course = allCourses.find(c => c.id === g.courseId);
                return (
                  <tr key={g.id}>
                    <td>{course ? course.name : g.courseId}</td>
                    <td>{g.examType}</td>
                    <td><strong style={{ color: g.grade >= 60 ? '#7B1FA2' : '#dc3545' }}>{g.grade}</strong></td>
                    <td>{g.date}</td>
                  </tr>
                );
              })}
              {grades.length === 0 && <tr><td colSpan={4} style={{ textAlign: 'center', color: '#999' }}>{t('noData')}</td></tr>}
            </tbody>
          </table>
          </div>
        )}

        {activeTab === 'payments' && (
          <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            <button className="btn btn-sm" style={{ background: '#4CAF50', color: '#fff', border: 'none' }}
              onClick={() => setShowChargeModal(true)}>💳 {t('collectPayment')}</button>
            <button className="btn btn-sm" style={{ background: '#1976D2', color: '#fff', border: 'none' }}
              onClick={() => setShowStandingOrderModal(true)}>🏦 {t('createOrder')}</button>
            <button className="btn btn-sm" style={{ background: '#FF9800', color: '#fff', border: 'none' }}
              onClick={() => setShowPaymentLinkModal(true)}>🔗 {t('sendLink')}</button>
            <button className="btn btn-sm" style={{ background: '#7B1FA2', color: '#fff', border: 'none' }}
              onClick={() => window.location.href = `/accounting?studentId=${id}`}>📄 {t('createInvoice')}</button>
          </div>
          <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('receiptNumber')}</th>
                <th>{t('amount')}</th>
                <th>{t('paidAmount')}</th>
                <th>{t('status')}</th>
                <th>{t('date')}</th>
              </tr>
            </thead>
            <tbody>
              {payments.map(p => (
                <tr key={p.id}>
                  <td>{p.receiptNumber || '-'}</td>
                  <td>{p.amount.toLocaleString()} ₪</td>
                  <td>{p.paidAmount.toLocaleString()} ₪</td>
                  <td><span className={`badge ${payStatusColors[p.status] || 'badge-gray'}`}>
                    {p.status === 'paid' ? t('paid') : p.status === 'partial' ? t('partial') : t('debt')}
                  </span></td>
                  <td>{p.date || '-'}</td>
                </tr>
              ))}
              {payments.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', color: '#999' }}>{t('noData')}</td></tr>}
            </tbody>
          </table>
          </div>
          </div>
        )}

        {activeTab === 'comm' && (
          <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>סוג</th>
                <th>{t('subject')}</th>
                <th>{t('message')}</th>
                <th>{t('date')}</th>
                <th>{t('performedBy')}</th>
              </tr>
            </thead>
            <tbody>
              {comms.map(c => (
                <tr key={c.id} onContextMenu={e => { e.preventDefault(); setCtxMenu({ x: e.clientX, y: e.clientY, type: 'comm', item: c }); }}>
                  <td><span className={`badge ${c.type === 'email' ? 'badge-blue' : 'badge-green'}`}>{c.type === 'email' ? '📧' : '📱'} {c.type}</span></td>
                  <td>{c.subject}</td>
                  <td style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.message}</td>
                  <td>{new Date(c.sentAt).toLocaleDateString('he-IL')}</td>
                  <td>{c.performedBy || '-'}</td>
                </tr>
              ))}
              {comms.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', color: '#999' }}>{t('noData')}</td></tr>}
            </tbody>
          </table>
          </div>
        )}

        {activeTab === 'calls' && (
          <div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <button className="btn btn-sm" style={{ background: '#4CAF50', color: '#fff', border: 'none' }} onClick={handleInitiateCall}>📞 {t('callNow')}</button>
              <button className="btn btn-sm" style={{ background: '#FF9800', color: '#fff', border: 'none' }} onClick={() => setShowScheduleCallModal(true)}>🕐 {t('scheduleCallBtn')}</button>
            </div>
            <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('date')}</th>
                  <th>{t('callTime')}</th>
                  <th>{t('callDuration')}</th>
                  <th>{t('calledBy')}</th>
                  <th>{t('callDirection')}</th>
                  <th>{t('callStatus')}</th>
                  <th>{t('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {callHistory.map((c: any) => (
                  <tr key={c.id}>
                    <td>{c.date}</td>
                    <td dir="ltr">{c.time}</td>
                    <td>{c.duration ? `${c.duration} ${t('sec')}` : '-'}</td>
                    <td>{c.calledBy || '-'}</td>
                    <td>
                      <span className={`badge ${c.direction === 'incoming' ? 'badge-blue' : 'badge-green'}`}>
                        {c.direction === 'incoming' ? `📥 ${t('incoming')}` : `📤 ${t('outgoing')}`}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${c.status === 'answered' ? 'badge-green' : c.status === 'no_answer' ? 'badge-red' : c.status === 'busy' ? 'badge-yellow' : c.status === 'scheduled' ? 'badge-blue' : 'badge-gray'}`}>
                        {t(c.status) || c.status}
                      </span>
                    </td>
                    <td>
                      {c.recordingUrl && (
                        <button className="btn btn-sm btn-outline" onClick={() => window.open(c.recordingUrl, '_blank')}>🎧 {t('listenRecording')}</button>
                      )}
                    </td>
                  </tr>
                ))}
                {callHistory.length === 0 && <tr><td colSpan={7} style={{ textAlign: 'center', color: '#999' }}>{t('noData')}</td></tr>}
              </tbody>
            </table>
            </div>
          </div>
        )}

        {activeTab === 'documents' && (
          <div>
            <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" onClick={() => setShowUploadModal(true)}>📤 {t('uploadDocument')}</button>
              <button className="btn" style={{ background: '#FF5722', color: '#fff', border: 'none' }} onClick={() => setShowSignatureModal(true)}>✍️ {t('sendForSignature')}</button>
            </div>
            <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('fileName')}</th>
                  <th>{t('docType')}</th>
                  <th>{t('uploadDate')}</th>
                  <th>{t('notes')}</th>
                  <th>{t('performedBy')}</th>
                  <th>{t('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {documents.map(doc => (
                  <tr key={doc.id} onContextMenu={e => { e.preventDefault(); setCtxMenu({ x: e.clientX, y: e.clientY, type: 'document', item: doc }); }}>
                    <td>{doc.fileName}</td>
                    <td><span className="badge badge-blue">{getDocTypeName(doc.docType)}</span></td>
                    <td>{new Date(doc.uploadDate).toLocaleDateString('he-IL')}</td>
                    <td>{doc.notes || '-'}</td>
                    <td>{doc.performedBy || '-'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <a
                          href={`${API_ORIGIN}/uploads/students/${id}/${doc.storedName}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-outline"
                        >👁️ {t('viewDoc')}</a>
                        <a
                          href={`${API_ORIGIN}/api/students/${id}/documents/${doc.id}/download`}
                          className="btn btn-sm"
                          style={{ backgroundColor: '#2196F3', color: '#fff', border: 'none' }}
                        >⬇️ {t('downloadDoc')}</a>
                        <button className="btn btn-sm btn-danger" onClick={() => handleDeleteDocument(doc.id)}>🗑️ {t('deleteDoc')}</button>
                      </div>
                    </td>
                  </tr>
                ))}
                {documents.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', color: '#999' }}>{t('noData')}</td></tr>}
              </tbody>
            </table>
            </div>
          </div>
        )}

        {activeTab === 'tasks' && id && (
          <TasksTab entityType="student" entityId={id} documents={documents} />
        )}

        {showUploadModal && (
          <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className="modal-title">📤 {t('uploadDocument')}</h3>
                <button className="modal-close" onClick={() => setShowUploadModal(false)}>×</button>
              </div>
              <div className="form-group">
                <label className="form-label">{t('docType')}</label>
                <select className="form-select" value={uploadDocType} onChange={e => setUploadDocType(e.target.value)}>
                  {docTypeKeys.map(key => (
                    <option key={key} value={key}>{getDocTypeName(key)}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{t('fileName')}</label>
                <input
                  type="file"
                  className="form-input"
                  onChange={e => setUploadFile(e.target.files?.[0] || null)}
                />
              </div>
              <div className="form-group">
                <label className="form-label">{t('notes')}</label>
                <textarea
                  className="form-textarea"
                  value={uploadNotes}
                  onChange={e => setUploadNotes(e.target.value)}
                />
              </div>
              <div className="modal-actions">
                <button className="btn btn-primary" onClick={handleUploadDocument} disabled={!uploadFile}>📤 {t('uploadDocument')}</button>
                <button className="btn btn-outline" onClick={() => setShowUploadModal(false)}>{t('cancel')}</button>
              </div>
            </div>
          </div>
        )}

        {/* Signature Tracking Table */}
        {activeTab === 'documents' && signatures.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <h4 style={{ color: '#7B1FA2', marginBottom: 12 }}>✍️ {t('signatureTracking')}</h4>
            <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('documents')}</th>
                  <th>{t('sentTo')}</th>
                  <th>{t('sentDate')}</th>
                  <th>{t('status')}</th>
                  <th>{t('signatureDate')}</th>
                </tr>
              </thead>
              <tbody>
                {signatures.map(sig => (
                  <tr key={sig.id}>
                    <td>{sig.documentName}</td>
                    <td>{sig.signerName} ({sig.signerEmail})</td>
                    <td>{new Date(sig.createdAt).toLocaleDateString('he-IL')}</td>
                    <td><span className={`badge ${sig.status === 'signed' ? 'badge-green' : sig.status === 'expired' ? 'badge-red' : 'badge-yellow'}`}>
                      {sig.status === 'signed' ? t('signed') : sig.status === 'expired' ? t('expired') : t('pending')}
                    </span></td>
                    <td>{sig.signedAt ? new Date(sig.signedAt).toLocaleDateString('he-IL') : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}

        {/* Charge Card Modal */}
        {showChargeModal && (
          <div className="modal-overlay" onClick={() => setShowChargeModal(false)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className="modal-title">💳 {t('collectPayment')}</h3>
                <button className="modal-close" onClick={() => setShowChargeModal(false)}>×</button>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">{t('amount')} (₪)</label>
                  <input className="form-input" type="number" value={chargeForm.amount} onChange={e => setChargeForm({...chargeForm, amount: parseInt(e.target.value) || 0})} />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('installments')}</label>
                  <input className="form-input" type="number" min="1" max="36" value={chargeForm.installments} onChange={e => setChargeForm({...chargeForm, installments: parseInt(e.target.value) || 1})} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">{t('description')}</label>
                <input className="form-input" value={chargeForm.description} onChange={e => setChargeForm({...chargeForm, description: e.target.value})} />
              </div>
              <div className="modal-actions">
                <button className="btn btn-primary" onClick={handleChargeCard}>💳 {t('chargeCard')}</button>
                <button className="btn btn-outline" onClick={() => setShowChargeModal(false)}>{t('cancel')}</button>
              </div>
            </div>
          </div>
        )}

        {/* Standing Order Modal */}
        {showStandingOrderModal && (
          <div className="modal-overlay" onClick={() => setShowStandingOrderModal(false)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className="modal-title">🏦 {t('createOrder')}</h3>
                <button className="modal-close" onClick={() => setShowStandingOrderModal(false)}>×</button>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">{t('monthlyAmount')} (₪)</label>
                  <input className="form-input" type="number" value={soForm.monthlyAmount} onChange={e => setSoForm({...soForm, monthlyAmount: parseInt(e.target.value) || 0})} />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('collectionDay')}</label>
                  <input className="form-input" type="number" min="1" max="28" value={soForm.collectionDay} onChange={e => setSoForm({...soForm, collectionDay: parseInt(e.target.value) || 1})} />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('numberOfMonths')}</label>
                  <input className="form-input" type="number" min="1" value={soForm.numberOfMonths} onChange={e => setSoForm({...soForm, numberOfMonths: parseInt(e.target.value) || 1})} />
                </div>
              </div>
              <h4 style={{ color: '#7B1FA2', margin: '12px 0' }}>{t('bankAccount')}</h4>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">{t('bankNumber')}</label>
                  <input className="form-input" value={soForm.bankNumber} onChange={e => setSoForm({...soForm, bankNumber: e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('branchNumber')}</label>
                  <input className="form-input" value={soForm.branchNumber} onChange={e => setSoForm({...soForm, branchNumber: e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('accountNumber')}</label>
                  <input className="form-input" value={soForm.accountNumber} onChange={e => setSoForm({...soForm, accountNumber: e.target.value})} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">{t('accountHolder')}</label>
                <input className="form-input" value={soForm.accountHolder} onChange={e => setSoForm({...soForm, accountHolder: e.target.value})} />
              </div>
              <div className="modal-actions">
                <button className="btn btn-primary" onClick={handleCreateStandingOrder}>🏦 {t('createStandingOrder')}</button>
                <button className="btn btn-outline" onClick={() => setShowStandingOrderModal(false)}>{t('cancel')}</button>
              </div>
            </div>
          </div>
        )}

        {/* Payment Link Modal */}
        {showPaymentLinkModal && (
          <div className="modal-overlay" onClick={() => { setShowPaymentLinkModal(false); setGeneratedLink(''); }}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className="modal-title">🔗 {t('sendLink')}</h3>
                <button className="modal-close" onClick={() => { setShowPaymentLinkModal(false); setGeneratedLink(''); }}>×</button>
              </div>
              {!generatedLink ? (
                <>
                  <div className="form-group">
                    <label className="form-label">{t('amount')} (₪)</label>
                    <input className="form-input" type="number" value={plForm.amount} onChange={e => setPlForm({...plForm, amount: parseInt(e.target.value) || 0})} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('description')}</label>
                    <input className="form-input" value={plForm.description} onChange={e => setPlForm({...plForm, description: e.target.value})} />
                  </div>
                  <div className="modal-actions">
                    <button className="btn btn-primary" onClick={handleCreatePaymentLink}>🔗 {t('sendPaymentLink')}</button>
                    <button className="btn btn-outline" onClick={() => setShowPaymentLinkModal(false)}>{t('cancel')}</button>
                  </div>
                </>
              ) : (
                <div>
                  <div style={{ background: '#f5f5f5', padding: 16, borderRadius: 8, marginBottom: 16, wordBreak: 'break-all', direction: 'ltr' }}>
                    {generatedLink}
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button className="btn btn-primary" onClick={() => { navigator.clipboard.writeText(generatedLink); alert('הקישור הועתק!'); }}>📋 {t('copyLink')}</button>
                    <button className="btn btn-whatsapp" onClick={() => {
                      if (student.phone) {
                        const waPhone = formatPhoneForWhatsApp(student.phone);
                        const text = encodeURIComponent(`שלום ${student.firstName}, הנה קישור לתשלום: ${generatedLink}`);
                        window.open(`https://wa.me/${waPhone}?text=${text}`, '_blank');
                      }
                    }}>📱 {t('sendViaWhatsApp')}</button>
                    <button className="btn btn-email" onClick={() => {
                      if (student.email) {
                        window.open(`mailto:${student.email}?subject=קישור לתשלום&body=שלום ${student.firstName}, הנה קישור לתשלום: ${generatedLink}`, '_blank');
                      }
                    }}>📧 {t('sendViaEmail')}</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Send for Signature Modal */}
        {showSignatureModal && (
          <div className="modal-overlay" onClick={() => setShowSignatureModal(false)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className="modal-title">✍️ {t('sendForSignature')}</h3>
                <button className="modal-close" onClick={() => setShowSignatureModal(false)}>×</button>
              </div>
              <div className="form-group">
                <label className="form-label">{t('selectDocument')}</label>
                <select className="form-select" value={sigForm.documentId} onChange={e => {
                  const doc = documents.find(d => d.id === e.target.value);
                  setSigForm({...sigForm, documentId: e.target.value, documentName: doc?.fileName || ''});
                }}>
                  <option value="">-- בחר מסמך --</option>
                  {documents.map(doc => <option key={doc.id} value={doc.id}>{doc.fileName}</option>)}
                </select>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">{t('signerName')}</label>
                  <input className="form-input" value={sigForm.signerName} onChange={e => setSigForm({...sigForm, signerName: e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('signerEmail')}</label>
                  <input className="form-input" type="email" value={sigForm.signerEmail} onChange={e => setSigForm({...sigForm, signerEmail: e.target.value})} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">{t('signerPhone')}</label>
                <input className="form-input" type="tel" value={sigForm.signerPhone} onChange={e => setSigForm({...sigForm, signerPhone: e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('attachedMessage')}</label>
                <textarea className="form-textarea" value={sigForm.message} onChange={e => setSigForm({...sigForm, message: e.target.value})} />
              </div>
              <div className="modal-actions">
                <button className="btn btn-primary" onClick={handleSendForSignature} disabled={!sigForm.documentId || !sigForm.signerName}>✍️ {t('sendForSignature')}</button>
                <button className="btn btn-outline" onClick={() => setShowSignatureModal(false)}>{t('cancel')}</button>
              </div>
            </div>
          </div>
        )}

        {/* Schedule Call Modal */}
        {showScheduleCallModal && (
          <div className="modal-overlay" onClick={() => setShowScheduleCallModal(false)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className="modal-title">🕐 {t('scheduleCallTitle')}</h3>
                <button className="modal-close" onClick={() => setShowScheduleCallModal(false)}>×</button>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">{t('scheduledDate')}</label>
                  <input className="form-input" type="date" value={scheduleCallForm.scheduledDate} onChange={e => setScheduleCallForm({...scheduleCallForm, scheduledDate: e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('scheduledTime')}</label>
                  <input className="form-input" type="time" value={scheduleCallForm.scheduledTime} onChange={e => setScheduleCallForm({...scheduleCallForm, scheduledTime: e.target.value})} />
                </div>
              </div>
              <div className="modal-actions">
                <button className="btn btn-primary" onClick={handleScheduleCall} disabled={!scheduleCallForm.scheduledDate || !scheduleCallForm.scheduledTime}>🕐 {t('scheduleCallBtn')}</button>
                <button className="btn btn-outline" onClick={() => setShowScheduleCallModal(false)}>{t('cancel')}</button>
              </div>
            </div>
          </div>
        )}

        {/* Context Menu */}
        {ctxMenu && ctxMenu.type === 'document' && (
          <ContextMenu
            x={ctxMenu.x} y={ctxMenu.y}
            onClose={() => setCtxMenu(null)}
            items={[
              { label: 'צור משימה ממסמך זה', icon: '📋', onClick: () => setCtxTaskModal({ sourceType: 'document', sourceName: ctxMenu.item.fileName, sourceLink: `${API_ORIGIN}/uploads/students/${id}/${ctxMenu.item.storedName}` }), dividerAfter: true },
              { label: 'פתח מסמך', icon: '👁️', onClick: () => window.open(`${API_ORIGIN}/uploads/students/${id}/${ctxMenu.item.storedName}`, '_blank') },
              { label: 'הורד מסמך', icon: '⬇️', onClick: () => { window.location.href = `${API_ORIGIN}/api/students/${id}/documents/${ctxMenu.item.id}/download`; } },
            ]}
          />
        )}
        {ctxMenu && ctxMenu.type === 'comm' && (
          <ContextMenu
            x={ctxMenu.x} y={ctxMenu.y}
            onClose={() => setCtxMenu(null)}
            items={[
              { label: 'צור משימה מהודעה זו', icon: '📋', onClick: () => setCtxTaskModal({ sourceType: 'communication', sourceName: ctxMenu.item.subject || ctxMenu.item.message?.substring(0, 40) || 'הודעה' }) },
            ]}
          />
        )}
        {ctxTaskModal && id && (
          <TaskFromContextModal
            entityType="student"
            entityId={id}
            sourceType={ctxTaskModal.sourceType}
            sourceName={ctxTaskModal.sourceName}
            sourceLink={ctxTaskModal.sourceLink}
            onClose={() => setCtxTaskModal(null)}
            onCreated={() => setActiveTab('tasks')}
          />
        )}
      </div>
    </div>
  );
}
